import { useEffect, useRef, useState } from "react";

function moeda(valor, casas = 0) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(Number(valor || 0));
}

function pct(valor, casas = 1) {
  return `${Number(valor || 0).toFixed(casas).replace(".", ",")}%`;
}

function primeiroNome(nome) {
  return String(nome || "Captador").trim().split(/\s+/)[0] || "Captador";
}

function dataCurta(valor) {
  if (!valor) return "—";
  const texto = String(valor).slice(0, 10);
  const partes = texto.split("-");
  if (partes.length !== 3) return texto;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function limitarPercentual(valor) {
  return Math.max(0, Math.min(100, Number(valor || 0)));
}

function Barra({ valor, cor = "bg-blue-600" }) {
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200/70 shadow-inner">
      <div
        className={`h-full rounded-full transition-all duration-700 ease-out ${cor}`}
        style={{ width: `${limitarPercentual(valor)}%` }}
      />
    </div>
  );
}

function CardMeta({
  titulo,
  percentual,
  vendido,
  meta,
  falta,
  atingida = false,
  loja = false,
}) {
  return (
    <div
      className={`relative min-w-0 overflow-hidden rounded-[22px] border p-3.5 shadow-sm ${
        loja
          ? "border-violet-200/70 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/50"
          : "border-blue-200/70 bg-gradient-to-br from-blue-50 via-white to-cyan-50/50"
      }`}
    >
      <div
        className={`absolute -right-7 -top-8 h-24 w-24 rounded-full blur-2xl ${
          loja ? "bg-violet-200/45" : "bg-blue-200/45"
        }`}
      />

      <div className="relative">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span
              className={`block text-[9px] font-black uppercase tracking-[0.14em] ${
                loja ? "text-violet-700" : "text-blue-800"
              }`}
            >
              {titulo}
            </span>
            <div className="mt-1.5 flex items-end gap-1.5">
              <strong className="block text-[28px] font-black leading-none tracking-[-0.055em] text-slate-950">
                {pct(percentual)}
              </strong>
              <span className="pb-0.5 text-[8px] font-extrabold uppercase text-slate-400">
                realizado
              </span>
            </div>
          </div>

          <span
            className={`rounded-full border px-2 py-1 text-[7px] font-black uppercase tracking-wide ${
              atingida
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : loja
                  ? "border-violet-100 bg-white/90 text-violet-600"
                  : "border-blue-100 bg-white/90 text-blue-600"
            }`}
          >
            {atingida ? "Meta batida ✓" : "Em andamento"}
          </span>
        </div>

        <div className="mt-3">
          <Barra
            valor={percentual}
            cor={loja ? "bg-violet-600" : "bg-blue-700"}
          />
        </div>

        <div className="mt-3 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <span className="block text-[7px] font-black uppercase tracking-wide text-slate-400">
              Produção
            </span>
            <strong className="mt-0.5 block truncate text-[14px] font-black text-slate-950">
              {moeda(vendido)}
            </strong>
            <span className="block truncate text-[9px] font-bold text-slate-500">
              meta {moeda(meta)}
            </span>
          </div>

          <div className="text-right">
            <span className="block text-[7px] font-black uppercase tracking-wide text-slate-400">
              {atingida ? "Status" : "Falta"}
            </span>
            <strong
              className={`mt-0.5 block whitespace-nowrap text-[12px] font-black ${
                atingida ? "text-emerald-700" : "text-orange-600"
              }`}
            >
              {atingida ? "Concluída" : moeda(falta)}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniMetrica({ label, valor, destaque = false }) {
  return (
    <div
      className={`min-w-0 rounded-xl border px-2 py-2.5 text-center ${
        destaque
          ? "border-blue-200 bg-blue-50/80"
          : "border-slate-200 bg-white"
      }`}
    >
      <span
        className={`block truncate text-[7px] font-black uppercase tracking-[0.09em] ${
          destaque ? "text-blue-600" : "text-slate-400"
        }`}
      >
        {label}
      </span>
      <strong
        className={`mt-1 block truncate text-[12px] font-black ${
          destaque ? "text-blue-900" : "text-slate-900"
        }`}
      >
        {valor}
      </strong>
    </div>
  );
}

function obterTokenBomDiaDaUrl() {
  if (typeof window === "undefined") return "";

  try {
    const hash = String(window.location.hash || "").replace(/^#/, "");
    if (!hash) return "";
    return String(new URLSearchParams(hash).get("bd") || "").trim();
  } catch {
    return "";
  }
}

function limparTokenBomDiaDaUrl() {
  if (typeof window === "undefined") return;

  try {
    const hash = String(window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(hash);
    if (!params.has("bd")) return;

    params.delete("bd");
    const novoHash = params.toString();
    const novaUrl =
      window.location.pathname +
      window.location.search +
      (novoHash ? `#${novoHash}` : "");

    window.history.replaceState(window.history.state, "", novaUrl);
  } catch {
    // A limpeza visual do token não pode impedir a abertura do painel.
  }
}

async function requestMeuDiaPorLink(token) {
  const response = await fetch("/api/bom-dia-producao/acesso-link", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-Bom-Dia-Token": token,
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      payload?.erro || payload?.message || "Não foi possível abrir seu Café com Dados.",
    );
  }

  return payload;
}


function CardOportunidade({
  id,
  emoji,
  titulo,
  quantidade,
  destaque,
  detalhe,
  tom = "blue",
  aberto = false,
  onClick,
}) {
  const tons = {
    blue: {
      barra: "bg-[#0f6cbd]",
      numero: "text-[#0b5cab]",
      icone: "text-[#0f6cbd]",
      ativo: "bg-[#f4f9fd]",
    },
    violet: {
      barra: "bg-[#5b5fc7]",
      numero: "text-[#4945a8]",
      icone: "text-[#5b5fc7]",
      ativo: "bg-[#f7f6ff]",
    },
    orange: {
      barra: "bg-[#d97706]",
      numero: "text-[#b85f00]",
      icone: "text-[#d97706]",
      ativo: "bg-[#fff9f2]",
    },
  };

  const c = tons[tom] || tons.blue;

  return (
    <button
      type="button"
      onClick={() => onClick?.(id)}
      className={`relative w-full overflow-hidden border border-slate-300 bg-white text-left transition hover:bg-slate-50 ${
        aberto ? c.ativo : ""
      }`}
    >
      <span className={`absolute inset-y-0 left-0 w-[4px] ${c.barra}`} />

      <div className="grid min-h-[76px] grid-cols-[42px_minmax(0,1fr)_104px_30px] items-center">
        <div className="flex h-full items-center justify-center border-r border-slate-200 bg-slate-50">
          <span className={`text-[16px] font-black ${c.icone}`}>{emoji}</span>
        </div>

        <div className="min-w-0 px-3.5 py-3">
          <span className="block truncate text-[11px] font-black uppercase tracking-[0.04em] text-slate-700">
            {titulo}
          </span>
          {destaque ? (
            <strong className="mt-1 block truncate text-[12px] font-black text-slate-900">
              {destaque}
            </strong>
          ) : null}
          {detalhe ? (
            <span className="mt-1 block truncate text-[10px] font-semibold text-slate-500">
              {detalhe}
            </span>
          ) : null}
        </div>

        <div className="border-l border-slate-200 px-2.5 py-3 text-right">
          <strong className={`block text-[27px] font-black leading-none ${c.numero}`}>
            {Number(quantidade || 0)}
          </strong>
          <span className="mt-1.5 block text-[8px] font-black uppercase tracking-wide text-slate-400">
            pessoas
          </span>
        </div>

        <div className="flex h-full items-center justify-center border-l border-slate-200 text-[15px] font-black text-slate-400">
          {aberto ? "−" : "+"}
        </div>
      </div>
    </button>
  );
}

function DetalheOportunidade({ tipo, oportunidades }) {
  if (!tipo) return null;

  const trazer = oportunidades?.paraTrazerClinica || {};
  const negociando = oportunidades?.negociando || {};
  const parcial = oportunidades?.fechadoParcial || {};

  const cfg = {
    trazer: {
      titulo: "Pessoas para trazer à clínica",
      itens: trazer.itens || [],
      render: (item) => (
        <>
          <span className="min-w-0 truncate">{item.nome}</span>
          <strong className="whitespace-nowrap text-blue-700">{item.status}</strong>
        </>
      ),
    },
    negociando: {
      titulo: "Negociando — orçamentos em aberto",
      itens: negociando.itens || [],
      render: (item) => (
        <>
          <span className="min-w-0 truncate">{item.nome}</span>
          <strong className="whitespace-nowrap text-violet-700">{moeda(item.totalOrcamento, 2)}</strong>
        </>
      ),
    },
    parcial: {
      titulo: "Fechado parcial — saldo a receber",
      itens: parcial.itens || [],
      render: (item) => (
        <>
          <div className="min-w-0">
            <span className="block truncate">{item.nome}</span>
            <span className="block text-[9px] font-bold text-slate-500">
              pago {moeda(item.valorPago, 2)}
            </span>
          </div>
          <strong className="whitespace-nowrap text-orange-700">
            falta {moeda(item.faltaPagar, 2)}
          </strong>
        </>
      ),
    },
  }[tipo];

  if (!cfg) return null;

  return (
    <div className="rounded-[18px] border border-slate-200 bg-[#fafbfc] p-2.5">
      <div className="flex items-center justify-between px-1 pb-1.5">
        <strong className="text-[10px] font-black text-slate-800">{cfg.titulo}</strong>
        <span className="text-[8px] font-bold uppercase text-slate-400">até 10</span>
      </div>

      {cfg.itens.length ? (
        <div className="grid gap-1">
          {cfg.itens.map((item, index) => (
            <div
              key={`${tipo}-${item.leadId || index}`}
              className="flex min-h-11 items-center justify-between gap-2 rounded-xl border border-slate-200/70 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
            >
              {cfg.render(item)}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl bg-slate-50 px-3 py-3 text-center text-[9px] font-bold text-slate-400">
          Nenhuma oportunidade nesta etapa.
        </div>
      )}
    </div>
  );
}


function DetalheRecuperacao({ tipo, recuperacao }) {
  if (!tipo) return null;

  const faltas = recuperacao?.faltouSemReagendar || {};
  const semLead = recuperacao?.chamouPrimeiroSemLead || {};

  const cfg = {
    faltou: {
      titulo: "Faltou e ainda não reagendou",
      itens: faltas.itens || [],
      render: (item) => (
        <>
          <div className="min-w-0">
            <span className="block truncate">{item.nome}</span>
            <span className="block text-[9px] font-bold text-slate-500">
              falta em {dataCurta(item.dataAgendamento)}
            </span>
          </div>
          <strong className="whitespace-nowrap text-red-700">reagendar</strong>
        </>
      ),
    },
    semlead: {
      titulo: "Chamou primeiro e ainda não virou lead",
      itens: semLead.itens || [],
      render: (item) => (
        <>
          <div className="min-w-0">
            <span className="block truncate">{item.telefone || "Sem telefone"}</span>
            <span
              className="block max-w-[245px] truncate text-[9px] font-bold text-slate-500"
              title={item.primeiraMensagem || ""}
            >
              {item.primeiraMensagem || "Primeira mensagem recebida do contato"}
            </span>
          </div>
          <strong className="whitespace-nowrap text-emerald-700">criar lead</strong>
        </>
      ),
    },
  }[tipo];

  if (!cfg) return null;

  return (
    <div className="rounded-[18px] border border-slate-200 bg-[#fafbfc] p-2.5">
      <div className="flex items-center justify-between px-1 pb-1.5">
        <strong className="text-[10px] font-black text-slate-800">{cfg.titulo}</strong>
        <span className="text-[8px] font-bold uppercase text-slate-400">até 10</span>
      </div>

      {cfg.itens.length ? (
        <div className="grid gap-1">
          {cfg.itens.map((item, index) => (
            <div
              key={`${tipo}-${item.leadId || item.chamadoId || index}`}
              className="flex min-h-11 items-center justify-between gap-2 rounded-xl border border-slate-200/70 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
            >
              {cfg.render(item)}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl bg-emerald-50 px-3 py-2 text-[9px] font-bold text-emerald-700">
          Nenhuma pendência neste indicador.
        </div>
      )}
    </div>
  );
}


function DetalheRotina({ tipo, rotina }) {
  if (!tipo) return null;

  const cfg = {
    atrasadas: {
      titulo: "Tarefas atrasadas",
      bloco: rotina?.atrasadas || {},
      acao: "resolver",
    },
    hoje: {
      titulo: "Vencendo hoje",
      bloco: rotina?.hoje || {},
      acao: "fazer hoje",
    },
    amanha: {
      titulo: "Vencendo amanhã",
      bloco: rotina?.amanha || {},
      acao: "preparar",
    },
    semTarefa: {
      titulo: "Leads sem tarefa pendente",
      bloco: rotina?.semTarefa || {},
      acao: "organizar",
    },
  }[tipo];

  if (!cfg) return null;

  const itens = Array.isArray(cfg.bloco?.itens) ? cfg.bloco.itens : [];

  return (
    <div className="rounded-[18px] border border-slate-200 bg-[#fafbfc] p-2.5">
      <div className="flex items-center justify-between px-1 pb-1.5">
        <strong className="text-[10px] font-black text-slate-800">
          {cfg.titulo}
        </strong>
        <span className="text-[8px] font-bold uppercase text-slate-400">
          até 10
        </span>
      </div>

      {itens.length ? (
        <div className="grid gap-1">
          {itens.map((item, index) => (
            <div
              key={`${tipo}-${item.leadId || index}`}
              className="flex min-h-11 items-center justify-between gap-2 rounded-xl border border-slate-200/70 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
            >
              <div className="min-w-0">
                <span className="block truncate">{item.nome}</span>
                <span className="block max-w-[250px] truncate text-[9px] font-bold text-slate-500">
                  {item.titulo || (tipo === "semTarefa" ? "sem tarefa pendente" : "tarefa pendente")}
                </span>
              </div>
              <strong className="whitespace-nowrap text-blue-700">
                {cfg.acao}
              </strong>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl bg-emerald-50 px-3 py-2 text-[9px] font-bold text-emerald-700">
          Nenhuma pendência neste indicador.
        </div>
      )}
    </div>
  );
}

export default function BomDiaProducaoModal({
  isOpen = false,
  empresaId,
  requestJson,
  onClose,
}) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [captadores, setCaptadores] = useState([]);
  const [podeSelecionar, setPodeSelecionar] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState("");
  const [oportunidadeAberta, setOportunidadeAberta] = useState("");
  const [recuperacaoAberta, setRecuperacaoAberta] = useState("");
  const [rotinaAberta, setRotinaAberta] = useState("");
  const tokenLinkRef = useRef("");
  const pollingRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;

    let ativo = true;

    const preparar = async () => {
      setCarregando(true);
      setErro("");
      setDados(null);

      try {
        const tokenLink = obterTokenBomDiaDaUrl();

        if (tokenLink) {
          tokenLinkRef.current = tokenLink;
          pollingRef.current = 0;
          const resposta = await requestMeuDiaPorLink(tokenLink);
          if (!ativo) return;

          setPodeSelecionar(false);
          setCaptadores([]);
          setUsuarioSelecionado("");
          setDados(resposta || null);
          limparTokenBomDiaDaUrl();
          return;
        }

        if (!empresaId || typeof requestJson !== "function") {
          throw new Error(
            "Sessão não encontrada. Abra novamente pelo WhatsApp ou faça login no CaptaCRM.",
          );
        }

        tokenLinkRef.current = "";
        pollingRef.current = 0;

        // Carrega contexto e dados ao mesmo tempo. /meu-dia já sabe identificar
        // o usuário logado e, para gestor, usa o primeiro captador quando nenhum
        // usuario_id é informado. Isso elimina uma ida-e-volta de rede.
        const [contexto, resposta] = await Promise.all([
          requestJson(
            `/api/bom-dia-producao/captadores?empresa_id=${encodeURIComponent(
              empresaId,
            )}`,
          ),
          requestJson(
            `/api/bom-dia-producao/meu-dia?empresa_id=${encodeURIComponent(
              empresaId,
            )}`,
          ),
        ]);

        if (!ativo) return;

        const lista = Array.isArray(contexto?.usuarios) ? contexto.usuarios : [];
        const selecionar = Boolean(contexto?.podeSelecionar);
        const alvoResposta = resposta?.visualizacao?.usuarioId;
        const alvo = alvoResposta
          ? String(alvoResposta)
          : lista.length
            ? String(lista[0].id)
            : "";

        setPodeSelecionar(selecionar);
        setCaptadores(lista);
        setUsuarioSelecionado(alvo);
        setDados(resposta || null);
      } catch (error) {
        if (ativo) {
          setErro(
            error?.message || "Não foi possível carregar o Café com Dados.",
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    preparar();

    return () => {
      ativo = false;
    };
  }, [isOpen, empresaId, requestJson]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const trocarCaptador = async (usuarioId) => {
    setUsuarioSelecionado(usuarioId);
    if (!usuarioId) return;

    tokenLinkRef.current = "";
    pollingRef.current = 0;
    setCarregando(true);
    setErro("");

    try {
      const resposta = await requestJson(
        `/api/bom-dia-producao/meu-dia?empresa_id=${encodeURIComponent(
          empresaId,
        )}&usuario_id=${encodeURIComponent(usuarioId)}`,
      );

      setDados(resposta || null);
    } catch (error) {
      setErro(error?.message || "Não foi possível carregar o usuário selecionado.");
    } finally {
      setCarregando(false);
    }
  };

  const statusBlocos = dados?.carregamentoBlocos || {};
  const blocosAindaCarregando = [
    statusBlocos.oportunidades,
    statusBlocos.recuperacao,
    statusBlocos.rotina,
  ].some((status) => status === "carregando" || status === "atualizando");

  const chavePolling = [
    statusBlocos.oportunidades || "",
    statusBlocos.recuperacao || "",
    statusBlocos.rotina || "",
    dados?.visualizacao?.usuarioId || usuarioSelecionado || "",
  ].join("|");

  useEffect(() => {
    if (!isOpen || !dados || !blocosAindaCarregando) return undefined;
    if (pollingRef.current >= 30) return undefined;

    let cancelado = false;
    const timer = window.setTimeout(async () => {
      try {
        let resposta = null;

        if (tokenLinkRef.current) {
          resposta = await requestMeuDiaPorLink(tokenLinkRef.current);
        } else if (empresaId && typeof requestJson === "function") {
          const alvo = dados?.visualizacao?.usuarioId || usuarioSelecionado || "";
          const queryUsuario = alvo
            ? `&usuario_id=${encodeURIComponent(alvo)}`
            : "";

          resposta = await requestJson(
            `/api/bom-dia-producao/meu-dia?empresa_id=${encodeURIComponent(
              empresaId,
            )}${queryUsuario}`,
          );
        }

        if (!cancelado && resposta) {
          pollingRef.current += 1;
          setDados(resposta);
        }
      } catch (error) {
        console.warn("Café: atualização em background falhou", error);
      }
    }, 700);

    return () => {
      cancelado = true;
      window.clearTimeout(timer);
    };
  }, [
    isOpen,
    chavePolling,
    blocosAindaCarregando,
    empresaId,
    usuarioSelecionado,
    requestJson,
  ]);

  if (!isOpen) return null;

  const producao = dados?.producao || {};
  const loja = dados?.loja || {};
  const comissao = dados?.comissao || {};
  const oportunidades = dados?.oportunidades || {};
  const recuperacao = dados?.recuperacao || {};
  const rotina = dados?.rotina || {};
  const tipoRelatorio = String(dados?.tipoRelatorio || "individual");
  const modoEquipe = tipoRelatorio === "equipe" || tipoRelatorio === "geral";
  const ranking = Array.isArray(dados?.equipe?.ranking)
    ? dados.equipe.ranking.slice(0, 3)
    : [];

  const temLoja = Number(loja?.meta || 0) > 0;
  const temComissao = !modoEquipe && Number(comissao?.metaIndividual || 0) > 0;
  const temOportunidades = !modoEquipe && Boolean(dados?.oportunidades);
  const temRecuperacao = !modoEquipe && Boolean(dados?.recuperacao);
  const temRotina = !modoEquipe && Boolean(dados?.rotina);
  const diferenca = Number(producao?.diferencaProjecaoInicioHoje || 0);
  const acimaRitmo = diferenca >= 0;
  const referenciaHoje = Number(producao?.referenciaHoje || 0);
  const progressoHoje = referenciaHoje > 0
    ? (Number(producao?.vendidoHoje || 0) / referenciaHoje) * 100
    : 100;

  return (
    <div
      className="fixed inset-0 z-[250] bg-slate-900/45 sm:flex sm:items-center sm:justify-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="relative h-[100dvh] w-full overflow-hidden bg-[#f3f3f3] sm:h-[96vh] sm:max-w-[560px] sm:rounded-[10px] sm:border sm:border-slate-400 sm:shadow-[0_24px_70px_rgba(15,23,42,.28)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-50 flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-lg font-light text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
          aria-label="Fechar Café com Dados"
          title="Fechar"
        >
          ×
        </button>

        <div className="h-full overflow-y-auto overscroll-contain">
          <header className="sticky top-0 z-30 border-b border-[#b8b8b8] bg-white shadow-[0_2px_8px_rgba(0,0,0,.06)]">
            <div className="h-1.5 w-full bg-[#0f6cbd]" />

            <div className="flex items-center justify-between gap-3 px-4 py-3 pr-12">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-[#0f6cbd] text-[19px] font-black text-white shadow-sm">
                  ☀
                </span>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black uppercase tracking-[0.14em] text-[#0f6cbd]">
                      Café com Dados
                    </span>
                    <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[7px] font-bold uppercase text-slate-500">
                      CaptaCRM
                    </span>
                  </div>

                  <h1 className="mt-0.5 truncate text-[18px] font-semibold tracking-[-0.02em] text-[#242424]">
                    Bom dia, {primeiroNome(dados?.nome)}
                  </h1>

                  <div className="mt-1 flex items-center gap-2 text-[9px] font-semibold text-slate-500">
                    <span>{dados?.data || "Hoje"}</span>
                    <span className="text-slate-300">•</span>
                    <span>{Number(producao?.diasTrabalhoRestantes || 0)} dias restantes</span>
                  </div>
                </div>
              </div>

              <div className="hidden text-right sm:block">
                <span className="block text-[8px] font-bold uppercase tracking-wide text-slate-400">
                  Atualizado
                </span>
                <strong className="mt-1 block text-[10px] font-semibold text-slate-600">
                  {dados?.horarioGerado || "--:--"}
                </strong>
              </div>
            </div>

            {podeSelecionar ? (
              <div className="border-t border-slate-200 bg-[#f8f8f8] px-4 py-2.5">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#0f6cbd]">
                    👤
                  </span>
                  <select
                    value={usuarioSelecionado}
                    onChange={(event) => trocarCaptador(event.target.value)}
                    disabled={carregando}
                    className="h-10 w-full appearance-none rounded-md border border-slate-300 bg-white pl-9 pr-8 text-[11px] font-semibold text-slate-800 outline-none focus:border-[#0f6cbd] focus:ring-1 focus:ring-[#0f6cbd]"
                  >
                    {captadores.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.nome}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-slate-400">
                    ▼
                  </span>
                </div>
              </div>
            ) : null}
          </header>

          {carregando && !dados ? (
            <div className="flex min-h-[65vh] items-center justify-center px-6">
              <div className="text-center">
                <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-blue-100 border-t-blue-700" />
                <p className="mt-3 text-[12px] font-bold text-slate-600">Atualizando seu dia...</p>
              </div>
            </div>
          ) : erro && !dados ? (
            <div className="flex min-h-[65vh] items-center justify-center px-7 text-center">
              <div>
                <div className="text-4xl">☁️</div>
                <h2 className="mt-3 text-lg font-black text-slate-900">Não consegui abrir seu dia</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">{erro}</p>
              </div>
            </div>
          ) : (
            <main className="space-y-3 p-3">
              {blocosAindaCarregando ? (
                <div className="flex items-center justify-between border border-[#d6e9f8] bg-[#eff7fc] px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-3 w-3 flex-none animate-spin rounded-full border-2 border-[#b9d9f2] border-t-[#0f6cbd]" />
                    <span className="truncate text-[8px] font-semibold text-[#0b5cab]">
                      Atualizando carteira e rotina
                    </span>
                  </div>
                  <span className="text-[7px] font-bold uppercase tracking-wide text-[#4f81a8]">
                    segundo plano
                  </span>
                </div>
              ) : null}

              {/* RESUMO EXECUTIVO */}
              <section className="border border-slate-300 bg-white">
                <div className="flex min-h-[38px] items-center justify-between border-b border-slate-300 bg-[#f4f4f4] px-3.5 py-2.5">
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">
                      Resumo executivo
                    </span>
                    <span className="ml-2 text-[9px] font-medium text-slate-500">
                      desempenho do mês
                    </span>
                  </div>
                  <span className="text-[10px] font-semibold text-slate-500">
                    {pct(producao.percentual)} realizado
                  </span>
                </div>

                <div className="grid grid-cols-2 divide-x divide-slate-300 border-b border-slate-300">
                  <div className="p-3.5">
                    <span className="block text-[8px] font-bold uppercase tracking-wide text-slate-500">
                      Minha produção
                    </span>
                    <strong className="mt-1 block truncate text-[30px] font-semibold leading-none tracking-[-0.03em] text-[#242424]">
                      {moeda(producao.vendidoMes, 2)}
                    </strong>
                    <div className="mt-3 h-2 overflow-hidden bg-[#e5e5e5]">
                      <div
                        className="h-full bg-[#0f6cbd] transition-all duration-700"
                        style={{ width: `${limitarPercentual(producao.percentual)}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-rows-2 divide-y divide-slate-300">
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <span className="text-[10px] font-semibold text-slate-500">
                        Meta
                      </span>
                      <strong className="text-[12px] font-semibold text-slate-900">
                        {moeda(producao.meta)}
                      </strong>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2.5">
                      <span className="text-[10px] font-semibold text-slate-500">
                        Falta
                      </span>
                      <strong className={`text-[11px] font-semibold ${
                        Number(producao.falta || 0) > 0 ? "text-[#c05a00]" : "text-emerald-700"
                      }`}>
                        {Number(producao.falta || 0) > 0 ? moeda(producao.falta) : "Meta batida"}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 divide-x divide-slate-300">
                  <div className="px-3 py-2.5">
                    <span className="block text-[8px] font-bold uppercase text-slate-400">
                      Meta da loja
                    </span>
                    <strong className="mt-1 block truncate text-[11px] font-semibold text-slate-800">
                      {temLoja ? pct(loja.percentual) : "—"}
                    </strong>
                  </div>
                  <div className="px-3 py-2.5">
                    <span className="block text-[8px] font-bold uppercase text-slate-400">
                      Precisa por dia
                    </span>
                    <strong className="mt-1 block truncate text-[11px] font-semibold text-slate-800">
                      {moeda(producao.metaDiariaNecessaria)}
                    </strong>
                  </div>
                  <div className="px-3 py-2.5">
                    <span className="block text-[8px] font-bold uppercase text-slate-400">
                      Ritmo
                    </span>
                    <strong className={`mt-1 block truncate text-[10px] font-semibold ${
                      acimaRitmo ? "text-emerald-700" : "text-[#c05a00]"
                    }`}>
                      {acimaRitmo ? "Acima" : "Abaixo"} {moeda(Math.abs(diferenca))}
                    </strong>
                  </div>
                </div>
              </section>

              {/* COMISSÃO */}
              {temComissao ? (
                <section className="border border-slate-300 bg-white">
                  <div className="flex min-h-[38px] items-center justify-between border-b border-slate-300 bg-[#f4f4f4] px-3.5 py-2.5">
                    <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">
                      Comissão
                    </span>
                    <span className="text-[8px] font-semibold text-emerald-700">
                      {pct(comissao.percentualAtual, 2)}
                    </span>
                  </div>

                  <div className="grid grid-cols-[1.2fr_.8fr] divide-x divide-slate-300">
                    <div className="p-3.5">
                      <span className="block text-[10px] font-semibold text-slate-500">
                        Comissão estimada hoje
                      </span>
                      <strong className="mt-1 block text-[27px] font-semibold leading-none text-[#242424]">
                        {moeda(comissao.valorAtual, 2)}
                      </strong>
                    </div>

                    <div className="p-3.5 text-right">
                      <span className="block text-[10px] font-semibold text-slate-500">
                        Potencial na meta
                      </span>
                      <strong className="mt-1 block text-[14px] font-semibold text-emerald-700">
                        +{moeda(comissao.potencialAmaisNaMeta, 2)}
                      </strong>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 divide-x divide-slate-300 border-t border-slate-300">
                    <div className="px-3 py-2.5 text-center">
                      <span className="block text-[8px] font-bold uppercase text-slate-400">
                        Hoje
                      </span>
                      <strong className="mt-1 block truncate text-[9px] font-semibold text-slate-800">
                        {moeda(comissao.valorAtual, 2)}
                      </strong>
                    </div>
                    <div className="px-3 py-2.5 text-center">
                      <span className="block text-[8px] font-bold uppercase text-slate-400">
                        Loja bate
                      </span>
                      <strong className="mt-1 block truncate text-[9px] font-semibold text-slate-800">
                        {moeda(comissao.valorSeLojaBaterHoje, 2)}
                      </strong>
                    </div>
                    <div className="px-3 py-2.5 text-center">
                      <span className="block text-[8px] font-bold uppercase text-slate-400">
                        Meta + loja
                      </span>
                      <strong className="mt-1 block truncate text-[9px] font-semibold text-emerald-700">
                        {moeda(comissao.valorNaMetaComLoja, 2)}
                      </strong>
                    </div>
                  </div>
                </section>
              ) : null}

              {/* OPORTUNIDADES */}
              {temOportunidades ? (
                <section className="border border-slate-300 bg-white">
                  <div className="flex min-h-[38px] items-center justify-between border-b border-slate-300 bg-[#f4f4f4] px-3.5 py-2.5">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">
                        Oportunidades
                      </span>
                      <span className="ml-2 text-[9px] font-medium text-slate-500">
                        onde está o próximo resultado
                      </span>
                    </div>
                    <span className="text-[8px] font-bold uppercase text-slate-400">
                      carteira atual
                    </span>
                  </div>

                  <div className="divide-y divide-slate-300">
                    <CardOportunidade
                      id="trazer"
                      emoji="↗"
                      titulo="Trazer para a clínica"
                      quantidade={oportunidades?.paraTrazerClinica?.quantidade}
                      destaque={`${Number(oportunidades?.paraTrazerClinica?.prospectando || 0)} prospectando • ${Number(
                        oportunidades?.paraTrazerClinica?.pendentes || 0
                      )} pendentes`}
                      detalhe="volume para avaliação"
                      tom="blue"
                      aberto={oportunidadeAberta === "trazer"}
                      onClick={(id) =>
                        setOportunidadeAberta((atual) => (atual === id ? "" : id))
                      }
                    />

                    <CardOportunidade
                      id="negociando"
                      emoji="R$"
                      titulo="Negociando"
                      quantidade={oportunidades?.negociando?.quantidade}
                      destaque={moeda(oportunidades?.negociando?.valorOrcamentos)}
                      detalhe="em orçamentos reais"
                      tom="violet"
                      aberto={oportunidadeAberta === "negociando"}
                      onClick={(id) =>
                        setOportunidadeAberta((atual) => (atual === id ? "" : id))
                      }
                    />

                    <CardOportunidade
                      id="parcial"
                      emoji="✓"
                      titulo="Fechado parcial"
                      quantidade={oportunidades?.fechadoParcial?.quantidade}
                      destaque={`${moeda(oportunidades?.fechadoParcial?.faltaPagar)} a receber`}
                      detalhe={`${moeda(oportunidades?.fechadoParcial?.valorPago)} já recebido`}
                      tom="orange"
                      aberto={oportunidadeAberta === "parcial"}
                      onClick={(id) =>
                        setOportunidadeAberta((atual) => (atual === id ? "" : id))
                      }
                    />
                  </div>

                  {oportunidadeAberta ? (
                    <div className="border-t border-slate-300 bg-[#fafafa] p-2.5">
                      <DetalheOportunidade
                        tipo={oportunidadeAberta}
                        oportunidades={oportunidades}
                      />
                    </div>
                  ) : null}
                </section>
              ) : null}

              {/* RECUPERAÇÃO */}
              {temRecuperacao ? (
                <section className="border border-slate-300 bg-white">
                  <div className="flex min-h-[38px] items-center justify-between border-b border-slate-300 bg-[#f4f4f4] px-3.5 py-2.5">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">
                        Recuperação
                      </span>
                      <span className="ml-2 text-[9px] font-medium text-slate-500">
                        interesse que não pode esfriar
                      </span>
                    </div>
                  </div>

                  <div className="divide-y divide-slate-300">
                    <CardOportunidade
                      id="faltou"
                      emoji="↻"
                      titulo="Faltou e não reagendou"
                      quantidade={recuperacao?.faltouSemReagendar?.quantidade}
                      destaque="recuperar agenda"
                      detalhe="já tinha aceitado vir à clínica"
                      tom="orange"
                      aberto={recuperacaoAberta === "faltou"}
                      onClick={(id) =>
                        setRecuperacaoAberta((atual) => (atual === id ? "" : id))
                      }
                    />

                    <CardOportunidade
                      id="semlead"
                      emoji="●"
                      titulo="Chamou primeiro"
                      quantidade={recuperacao?.chamouPrimeiroSemLead?.quantidade}
                      destaque="ainda não virou lead"
                      detalhe="o contato iniciou a conversa"
                      tom="blue"
                      aberto={recuperacaoAberta === "semlead"}
                      onClick={(id) =>
                        setRecuperacaoAberta((atual) => (atual === id ? "" : id))
                      }
                    />
                  </div>

                  {recuperacaoAberta ? (
                    <div className="border-t border-slate-300 bg-[#fafafa] p-2.5">
                      <DetalheRecuperacao
                        tipo={recuperacaoAberta}
                        recuperacao={recuperacao}
                      />
                    </div>
                  ) : null}
                </section>
              ) : null}

              {/* ROTINA */}
              {temRotina ? (
                <section className="border border-slate-300 bg-white">
                  <div className="flex min-h-[42px] items-center justify-between border-b border-slate-300 bg-[#f4f4f4] px-3.5 py-2.5">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">
                        Rotina comercial
                      </span>
                      <span className="ml-2 text-[9px] font-medium text-slate-500">
                        o que precisa da sua atenção
                      </span>
                    </div>
                    <span className="rounded border border-slate-300 bg-white px-2 py-1 text-[8px] font-bold uppercase text-slate-500">
                      tarefas
                    </span>
                  </div>

                  <div className="divide-y divide-slate-300">
                    <CardOportunidade
                      id="atrasadas"
                      emoji="!"
                      titulo="Tarefas atrasadas"
                      quantidade={rotina?.atrasadas?.quantidade}
                      destaque="Resolver agora"
                      detalhe="tarefas que já passaram do vencimento"
                      tom="orange"
                      aberto={rotinaAberta === "atrasadas"}
                      onClick={(id) =>
                        setRotinaAberta((atual) => (atual === id ? "" : id))
                      }
                    />

                    <CardOportunidade
                      id="hoje"
                      emoji="•"
                      titulo="Vencendo hoje"
                      quantidade={rotina?.hoje?.quantidade}
                      destaque="Prioridade do dia"
                      detalhe="tarefas que precisam ser concluídas hoje"
                      tom="orange"
                      aberto={rotinaAberta === "hoje"}
                      onClick={(id) =>
                        setRotinaAberta((atual) => (atual === id ? "" : id))
                      }
                    />

                    <CardOportunidade
                      id="amanha"
                      emoji="→"
                      titulo="Vencendo amanhã"
                      quantidade={rotina?.amanha?.quantidade}
                      destaque="Próximas ações"
                      detalhe="prepare a agenda antes de vencer"
                      tom="blue"
                      aberto={rotinaAberta === "amanha"}
                      onClick={(id) =>
                        setRotinaAberta((atual) => (atual === id ? "" : id))
                      }
                    />

                    <CardOportunidade
                      id="semTarefa"
                      emoji="○"
                      titulo="Leads sem tarefa"
                      quantidade={rotina?.semTarefa?.quantidade}
                      destaque="Organizar carteira"
                      detalhe="leads sem uma próxima ação definida"
                      tom="blue"
                      aberto={rotinaAberta === "semTarefa"}
                      onClick={(id) =>
                        setRotinaAberta((atual) => (atual === id ? "" : id))
                      }
                    />
                  </div>

                  {rotinaAberta ? (
                    <div className="border-t border-slate-300 bg-[#fafafa] p-3">
                      <DetalheRotina tipo={rotinaAberta} rotina={rotina} />
                    </div>
                  ) : null}
                </section>
              ) : null}

              {/* PRODUÇÃO DO DIA */}
              <section className="border border-slate-300 bg-white">
                <div className="flex min-h-[38px] items-center justify-between border-b border-slate-300 bg-[#f4f4f4] px-3.5 py-2.5">
                  <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">
                    Produção de hoje
                  </span>
                  <span className="text-[10px] font-semibold text-slate-500">
                    referência {moeda(producao.referenciaHoje)}
                  </span>
                </div>

                <div className="grid grid-cols-[1fr_130px] divide-x divide-slate-300">
                  <div className="p-3.5">
                    <strong className="block text-[26px] font-semibold leading-none text-[#242424]">
                      {moeda(producao.vendidoHoje, 2)}
                    </strong>
                    <div className="mt-3 h-2 overflow-hidden bg-[#e5e5e5]">
                      <div
                        className="h-full bg-[#0f6cbd] transition-all duration-700"
                        style={{ width: `${limitarPercentual(progressoHoje)}%` }}
                      />
                    </div>
                  </div>

                  <div className="p-3.5 text-right">
                    <span className="block text-[10px] font-semibold text-slate-500">
                      Falta hoje
                    </span>
                    <strong className={`mt-1 block text-[11px] font-semibold ${
                      Number(producao.faltaHoje || 0) > 0 ? "text-[#c05a00]" : "text-emerald-700"
                    }`}>
                      {Number(producao.faltaHoje || 0) > 0
                        ? moeda(producao.faltaHoje, 2)
                        : "Meta do dia"}
                    </strong>
                  </div>
                </div>
              </section>

              {modoEquipe ? (
                <section className="border border-slate-300 bg-white">
                  <div className="border-b border-slate-300 bg-[#f7f7f7] px-3 py-2">
                    <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">
                      Equipe
                    </span>
                  </div>
                  <div className="divide-y divide-slate-300">
                    {ranking.map((item, index) => (
                      <div key={item.id || index} className="flex items-center justify-between px-3 py-2">
                        <span className="truncate text-[9px] font-medium text-slate-600">
                          {index + 1}. {item.nome}
                        </span>
                        <strong className="text-[9px] font-semibold text-slate-900">
                          {moeda(item.vendidoMes, 2)}
                        </strong>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <div className="flex items-center justify-between border-t border-slate-300 px-1 pt-2 text-[7px] font-bold uppercase tracking-[0.08em] text-slate-400">
                <span>CaptaCRM • Café com Dados</span>
                <span>{dados?.horarioGerado || "--:--"}</span>
              </div>

              {carregando && dados ? (
                <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-white/20 sm:absolute">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#0f6cbd]" />
                </div>
              ) : null}

              {erro && dados ? (
                <p className="border border-amber-200 bg-amber-50 px-3 py-2 text-center text-[9px] font-semibold text-amber-700">
                  {erro}
                </p>
              ) : null}
            </main>
          )}
        </div>
      </div>
    </div>
  );
}
